import { useEffect, useState } from "react";
import { Bell, MousePointerClick, BarChart3, Download, Eye, Globe2, Languages, Loader2, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

const Total = ({ icon: Icon, label, value, testId }) => (
  <div className="min-w-0 rounded-xl bg-black/30 p-3">
    <p className="flex min-w-0 items-center gap-1.5 break-words text-[0.7rem] uppercase tracking-wider text-neutral-400"><Icon className="h-3.5 w-3.5 shrink-0" /> {label}</p>
    <p className="mt-1 font-display text-xl font-bold text-white" data-testid={testId}>{value}</p>
  </div>
);

// 30-day bars (views, unique visitors as overlay) + downloads line of small bars.
const Chart = ({ days, k, tone, testId }) => {
  const { fmtNum } = useI18n();
  const max = Math.max(1, ...days.map((d) => d[k]));
  return (
    <div className="flex h-24 min-w-0 items-end gap-[2px]" dir="ltr" data-testid={testId} role="img" aria-label={testId}>
      {days.map((d) => (
        <div key={d.day} title={`${d.day} · ${fmtNum(d[k])}`} className={`min-w-0 flex-1 rounded-t-sm ${tone}`} style={{ height: `${Math.max(2, (d[k] / max) * 100)}%`, opacity: d[k] ? 1 : 0.25 }} />
      ))}
    </div>
  );
};

const Top = ({ title, icon: Icon, rows, label, testId }) => {
  const { fmtNum } = useI18n();
  const max = Math.max(1, ...rows.map((r) => r.n));
  return (
    <div className="min-w-0 space-y-2" data-testid={testId}>
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-neutral-400"><Icon className="h-3.5 w-3.5" /> {title}</p>
      {rows.length === 0 && <p className="text-xs text-neutral-500">—</p>}
      <ul className="space-y-1">
        {rows.map((r) => (
          <li key={r.key} className="min-w-0 text-xs" data-testid={`${testId}-row`}>
            <div className="flex min-w-0 items-baseline justify-between gap-2"><span className="min-w-0 break-all text-neutral-200" dir="ltr">{label(r.key)}</span><span className="shrink-0 tabular-nums text-white">{fmtNum(r.n)}</span></div>
            <div className="mt-0.5 h-1 rounded-full bg-neutral-800"><div className="h-full rounded-full bg-[var(--gold)]" style={{ width: `${(r.n / max) * 100}%` }} /></div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const StatsCard = () => {
  const { t, fmtNum, fmtDate, locale } = useI18n();
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setS(data)).catch(() => setS(false));
  }, []);
  const country = (c) => {
    if (c === "none") return t("stats.unknown");
    try {
      return new Intl.DisplayNames([locale], { type: "region" }).of(c) || c;
    } catch {
      return c;
    }
  };
  const lang = (c) => (c === "other" ? t("stats.other") : (() => { try { return new Intl.DisplayNames([locale], { type: "language" }).of(c); } catch { return c; } })());
  if (s === null) return <Loader2 className="h-6 w-6 animate-spin text-[var(--gold)]" data-testid="stats-loading" />;
  if (!s) return <p className="text-sm text-red-300" data-testid="stats-error">{t("stats.error")}</p>;
  const today = s.days[s.days.length - 1];
  return (
    <section className="card-carbon min-w-0 space-y-5 rounded-2xl p-5" data-testid="stats-card">
      <div className="min-w-0">
        <h2 className="flex items-center gap-2 font-display text-base uppercase tracking-wider text-[var(--gold-bright)] md:text-lg"><BarChart3 className="h-5 w-5 shrink-0" /> {t("stats.title")}</h2>
        <p className="mt-1 max-w-3xl break-words text-xs text-neutral-400">{t("stats.privacy")}{s.since && ` · ${t("stats.since", { date: fmtDate(s.since) })}`}</p>
      </div>
      <div className="grid min-w-0 grid-cols-2 gap-2 lg:grid-cols-4">
        <Total icon={Eye} label={t("stats.viewsTotal")} value={fmtNum(s.total.views)} testId="stats-total-views" />
        <Total icon={Users} label={t("stats.uniquesToday")} value={fmtNum(today.uniques)} testId="stats-today-uniques" />
        <Total icon={Eye} label={t("stats.viewsToday")} value={fmtNum(today.views)} testId="stats-today-views" />
        <Total icon={Download} label={t("stats.downloadsTotal")} value={fmtNum(s.total.downloads)} testId="stats-total-downloads" />
        <Total icon={Bell} label={t("stats.pushOptins")} value={fmtNum(s.total.push_optins || 0)} testId="stats-total-push-optins" />
        <Total icon={MousePointerClick} label={t("stats.pushClicks")} value={fmtNum(s.total.push_clicks || 0)} testId="stats-total-push-clicks" />
      </div>
      <div className="min-w-0 space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400">{t("stats.chartViews")}</p>
        <Chart days={s.days} k="views" tone="bg-[var(--gold)]" testId="stats-chart-views" />
        <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400">{t("stats.chartUniques")}</p>
        <Chart days={s.days} k="uniques" tone="bg-sky-400" testId="stats-chart-uniques" />
        <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400">{t("stats.chartDownloads")}</p>
        <Chart days={s.days} k="downloads" tone="bg-emerald-400" testId="stats-chart-downloads" />
        <div className="flex justify-between text-[0.65rem] text-neutral-500" dir="ltr"><span>{fmtDate(s.days[0].day)}</span><span>{fmtDate(today.day)}</span></div>
      </div>
      <div className="grid min-w-0 gap-5 md:grid-cols-3">
        <Top title={t("stats.countries")} icon={Globe2} rows={s.countries} label={country} testId="stats-countries" />
        <Top title={t("stats.langs")} icon={Languages} rows={s.langs} label={lang} testId="stats-langs" />
        <Top title={t("stats.pages")} icon={Eye} rows={s.pages} label={(p) => p} testId="stats-pages" />
      </div>
    </section>
  );
};
